import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';

interface Language {
  code: string;
  name: string;
  flag: string;
}

export const AVAILABLE_LANGUAGES: Language[] = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
  { code: 'no', name: 'Norsk', flag: '🇳🇴' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰' },
  { code: 'fi', name: 'Suomi', flag: '🇫🇮' },
  { code: 'cs', name: 'Čeština', flag: '🇨🇿' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
  { code: 'bg', name: 'Български', flag: '🇧🇬' },
  { code: 'el', name: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'he', name: 'עברית', flag: '🇮🇱' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'ms', name: 'Bahasa Melayu', flag: '🇲🇾' },
  { code: 'uk', name: 'Українська', flag: '🇺🇦' },
];

interface LanguageSelectorProps {
  selectedLanguages: string[];
  onChange: (_languages: string[]) => void;
  defaultLanguage?: string;
}

export function LanguageSelector({ selectedLanguages, onChange, defaultLanguage = 'en' }: LanguageSelectorProps) {
  const { t } = useTranslation();
  const [showAddDialog, setShowAddDialog] = useState(false);

  const handleAddLanguage = (languageCode: string) => {
    if (!selectedLanguages.includes(languageCode)) {
      onChange([...selectedLanguages, languageCode]);
    }
    setShowAddDialog(false);
  };

  const handleRemoveLanguage = (languageCode: string) => {
    if (languageCode !== defaultLanguage) {
      onChange(selectedLanguages.filter(code => code !== languageCode));
    }
  };

  const selectedLanguageObjects = selectedLanguages
    .map(code => AVAILABLE_LANGUAGES.find(lang => lang.code === code))
    .filter(Boolean) as Language[];

  const availableToAdd = AVAILABLE_LANGUAGES.filter(
    lang => !selectedLanguages.includes(lang.code)
  );

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('products.supportedLanguages')}</CardTitle>
          <CardDescription>
            {t('products.supportedLanguagesDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {selectedLanguageObjects.map(lang => (
                <Badge
                  key={lang.code}
                  variant="secondary"
                  className="px-3 py-2 flex items-center gap-2"
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span>{lang.name}</span>
                  {lang.code === defaultLanguage && (
                    <span className="text-xs text-muted-foreground ml-1">({t('common.default')})</span>
                  )}
                  {lang.code !== defaultLanguage && (
                    <button
                      onClick={() => handleRemoveLanguage(lang.code)}
                      className="ml-1 hover:bg-background/20 rounded p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddDialog(true)}
                className="h-[42px]"
              >
                <Plus className="h-4 w-4 mr-1" />
                {t('products.addLanguage')}
              </Button>
            </div>
            
            <p className="text-sm text-muted-foreground">
              {t('products.languageSelectorHint')}
            </p>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('products.selectLanguage')}</DialogTitle>
            <DialogDescription>
              {t('products.selectLanguageDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="h-[400px] overflow-y-auto pr-4">
            <div className="grid grid-cols-2 gap-2">
              {availableToAdd.map(lang => (
                <Button
                  key={lang.code}
                  variant="outline"
                  className="justify-start h-auto py-3"
                  onClick={() => handleAddLanguage(lang.code)}
                >
                  <span className="text-2xl mr-3">{lang.flag}</span>
                  <div className="text-left">
                    <div className="font-medium">{lang.name}</div>
                    <div className="text-xs text-muted-foreground">{lang.code.toUpperCase()}</div>
                  </div>
                </Button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
